import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import theme from 'assets/theme'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormDropdown from 'components/KNForm/KNFormDropdown'
import KNFormText from 'components/KNForm/KNFormText'
import KNMapLibre from 'components/KNMapLibre/KNMapLibre'
import { type MapSelection, MapSelectionMode } from 'components/KNMapLibre/types'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processServerErrorMessages } from 'global/helpers/form'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useEffect, useState } from 'react'
import { FormProvider, type SubmitHandler, useForm, type ValidateResult } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AreaNameDialog from './AreaNameDialog'
import { areasToMapSelections, isAreaAlreadySelected, mapSelectionToArea, minAreasValidator } from './CarrierProfile.helpers'
import { createLane, editLane, LANES_QUERY_KEY } from './CarrierProfile.service'
import { type Area, type Contact, type Lane, LaneDirection, type LaneFormValues, LaneOrRegionPanel } from './CarrierProfile.types'
import LaneOrRegionDialogActions from './LaneOrRegionDialogActions'
import LaneOrRegionDialogAreas from './LaneOrRegionDialogAreas'
import LaneOrRegionDialogContacts from './LaneOrRegionDialogContacts'
import LaneOrRegionDialogModeSwitcher from './LaneOrRegionDialogModeSwitcher'
import LaneOrRegionDialogPanel from './LaneOrRegionDialogPanel'

interface CreateOrEditLaneDialogPayload {
  lane?: Lane
  contacts: Contact[]
}

export interface CreateOrEditLaneDialogProps {
  payload: CreateOrEditLaneDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

const CreateOrEditLaneDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: CreateOrEditLaneDialogProps): ReactElement => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [activePanel, setActivePanel] = useState(LaneOrRegionPanel.START)
  const form = useForm<LaneFormValues>({
    defaultValues: {
      startLocation: {
        areas: [],
      },
      endLocation: {
        areas: [],
      },
      contactIds: [],
    },
    criteriaMode: 'all',
  })
  const { watch, trigger, handleSubmit, reset, control, formState, getValues, setValue, setError } = form
  const createLaneMutation = useMutation({
    mutationFn: async (data: LaneFormValues) => {
      await createLane(data)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LANES_QUERY_KEY] }),
  })
  const updateLaneMutation = useMutation({
    mutationFn: async (data: LaneFormValues) => {
      await editLane(payload.lane?.id ?? '', data)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LANES_QUERY_KEY] }),
  })
  const watchedStartAreas = watch('startLocation.areas')
  const watchedEndAreas = watch('endLocation.areas')
  const [selectionMode, setSelectionMode] = useState<MapSelectionMode>()
  const [selections, setSelections] = useState<MapSelection[]>([])
  const [recentSelection, setRecentSelection] = useState<MapSelection>()
  const [autoStartLocationName, setAutoStartLocationName] = useState('')
  const [autoEndLocationName, setAutoEndLocationName] = useState('')
  const [endLocationPanelVisible, setEndLocationPanelVisible] = useState(!!payload.lane)
  const [additionalInfoPanelVisible, setAdditionalInfoPanelVisible] = useState(!!payload.lane)

  const handleAreaNameDialogAction = useCallback((newArea: Area) => {
    if (activePanel === LaneOrRegionPanel.START) {
      const startAreas = getValues('startLocation.areas')
      setValue('startLocation.areas', [...startAreas, newArea])
    } else if (activePanel === LaneOrRegionPanel.END) {
      const endAreas = getValues('endLocation.areas')
      setValue('endLocation.areas', [...endAreas, newArea])
    }
  }, [activePanel])

  const handleAreaNameDialogClose = useCallback(() => {
    setSelectionMode(undefined)
  }, [])

  const areaNameDialog = useDialog<typeof AreaNameDialog>({
    onAction: handleAreaNameDialogAction,
    onClose: handleAreaNameDialogClose,
  })

  useEffect(() => {
    if (open) {
      reset({
        startLocation: {
          name: payload.lane?.startLocation.name,
          areas: payload.lane?.startLocation.areas ?? [],
        },
        endLocation: {
          name: payload.lane?.endLocation.name,
          areas: payload.lane?.endLocation.areas ?? [],
        },
        direction: payload.lane?.direction,
        vehiclesAmount: payload.lane?.vehiclesAmount,
        contactIds: payload.lane?.contacts.map((contact) => contact.id) ?? [],
      })
      setActivePanel(LaneOrRegionPanel.START)
      setAutoStartLocationName('')
      setAutoEndLocationName('')
      setEndLocationPanelVisible(!!payload.lane)
      setAdditionalInfoPanelVisible(!!payload.lane)
    }
  }, [open])

  const handlePanelChange = useCallback((panel: LaneOrRegionPanel) => {
    setActivePanel(panel)
  }, [])

  const processAutoLocationNames = useCallback(() => {
    if (!getValues('startLocation.name')) {
      setValue('startLocation.name', autoStartLocationName, { shouldValidate: true })
    }
    if (!getValues('endLocation.name')) {
      setValue('endLocation.name', autoEndLocationName, { shouldValidate: true })
    }
  }, [autoStartLocationName, autoEndLocationName])

  const onSubmit: SubmitHandler<LaneFormValues> = async (data: LaneFormValues): Promise<void> => {
    await handleFormSubmission(
      () => payload.lane ? updateLaneMutation.mutateAsync(data) : createLaneMutation.mutateAsync(data),
      {
        onError: (error) => setError('root', processServerErrorMessages(error)),
        onSuccess: () => {
          if (payload.lane) {
            analyticsEvent('polestar_carrier_profile_lane_updated')
          } else {
            analyticsEvent('polestar_carrier_profile_lane_created')
          }
          onAction()
        },
      },
    )
  }

  const handleSubmitWrapper = useCallback(() => {
    processAutoLocationNames()
    void handleSubmit(onSubmit)()
  }, [processAutoLocationNames, onSubmit])

  useEffect(() => {
    setSelections([
      ...areasToMapSelections(watchedStartAreas, theme.palette.secondary.main),
      ...areasToMapSelections(watchedEndAreas, theme.palette.primary.main),
    ])
    setSelectionMode(undefined)
    setAutoStartLocationName(watchedStartAreas.map((area) => area.name).join(', '))
    setAutoEndLocationName(watchedEndAreas.map((area) => area.name).join(', '))
  }, [watchedStartAreas, watchedEndAreas])

  const handleSelectionModeChange = useCallback((mode?: MapSelectionMode) => {
    setSelectionMode(mode)
  }, [])

  const handleSelection = useCallback((selection: MapSelection) => {
    setRecentSelection(selection)
    if (selection.mode === MapSelectionMode.CIRCLE) {
      areaNameDialog.handleOpen()
      return
    }
    if (activePanel === LaneOrRegionPanel.START) {
      const startAreas = getValues('startLocation.areas')
      const alreadySelectedArea = startAreas.find((area) => isAreaAlreadySelected(area, selection))
      const newArea = mapSelectionToArea(selection)
      if (!alreadySelectedArea && newArea) {
        setValue('startLocation.areas', [...startAreas, newArea])
      }
    } else if (activePanel === LaneOrRegionPanel.END) {
      const endAreas = getValues('endLocation.areas')
      const alreadySelectedArea = endAreas.find((area) => isAreaAlreadySelected(area, selection))
      const newArea = mapSelectionToArea(selection)
      if (!alreadySelectedArea && newArea) {
        setValue('endLocation.areas', [...endAreas, newArea])
      }
    }
    setSelectionMode(undefined)
  }, [activePanel])

  const showEndLocationPanel = useCallback(async () => {
    processAutoLocationNames()
    if (await trigger(['startLocation.areas'])) {
      setEndLocationPanelVisible(true)
      setActivePanel(LaneOrRegionPanel.END)
    }
  }, [processAutoLocationNames])

  const showAdditionalInfoPanel = useCallback(async () => {
    processAutoLocationNames()
    if (await trigger(['endLocation.areas', 'direction'])) {
      setAdditionalInfoPanelVisible(true)
      setActivePanel(LaneOrRegionPanel.ADDITIONAL_INFO)
    }
  }, [processAutoLocationNames])

  return (
    <FormProvider {...form}>
      <KNDialog
        open={open}
        onClose={onClose}
        closeAction={false}
        maxWidth="xxl"
        preventClosing={formState.isSubmitting}
      >
        <Stack direction="row" spacing={2} height="90vh">
          <Box sx={{ margin: '-1rem 0 -1rem -1rem !important', flexGrow: 1 }}>
            <KNMapLibre
              withSelectors
              selectionSource="rcs_countries"
              selectionMode={selectionMode}
              selections={selections}
              onSelectionChange={handleSelection}
              withoutLayersControl
              customAttribution={t('screens.cs.carrier_profile.lanes_and_regions.disclaimer')}
            />
          </Box>

          <Stack direction="column" spacing={1} sx={{ width: '360px' }}>
            <Typography variant="h6" color={theme.palette.primary.main}>
              {payload.lane
                ? t('screens.cs.carrier_profile.lanes_and_regions.form.edit_lane')
                : t('screens.cs.carrier_profile.lanes_and_regions.form.add_new_lane')}
            </Typography>
            <KNDialogFormErrors errors={formState.errors?.root} />
            <Box sx={{ flexGrow: 1 }}>
              <LaneOrRegionDialogPanel
                panelType={LaneOrRegionPanel.START}
                header={t(`screens.cs.carrier_profile.lanes_and_regions.start_location`)}
                activePanel={activePanel}
                onPanelChange={handlePanelChange}
              >
                <KNFormText
                  name="startLocation.name"
                  label={t('screens.cs.carrier_profile.lanes_and_regions.form.name')}
                  placeholder={autoStartLocationName}
                  control={control}
                />
                <LaneOrRegionDialogAreas
                  name="startLocation.areas"
                  label={t('screens.cs.carrier_profile.lanes_and_regions.form.areas')}
                  control={control}
                  rules={{
                    validate: {
                      minAreas: (): ValidateResult => minAreasValidator(getValues('startLocation.areas')),
                    },
                  }}
                />
                <LaneOrRegionDialogModeSwitcher mode={selectionMode} onModeChange={handleSelectionModeChange} />
                {!endLocationPanelVisible && (
                  <Box paddingTop={2}>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={showEndLocationPanel}
                      fullWidth
                    >
                      {t('general.next')}
                    </Button>
                  </Box>
                )}
              </LaneOrRegionDialogPanel>
              {endLocationPanelVisible && (
                <LaneOrRegionDialogPanel
                  panelType={LaneOrRegionPanel.END}
                  header={t(`screens.cs.carrier_profile.lanes_and_regions.end_location`)}
                  activePanel={activePanel}
                  onPanelChange={handlePanelChange}
                >
                  <KNFormText
                    name="endLocation.name"
                    label={t('screens.cs.carrier_profile.lanes_and_regions.form.name')}
                    placeholder={autoEndLocationName}
                    control={control}
                  />
                  <LaneOrRegionDialogAreas
                    name="endLocation.areas"
                    label={t('screens.cs.carrier_profile.lanes_and_regions.form.areas')}
                    control={control}
                    rules={{
                      validate: {
                        minAreas: (): ValidateResult => minAreasValidator(getValues('endLocation.areas')),
                      },
                    }}
                  />
                  <LaneOrRegionDialogModeSwitcher mode={selectionMode} onModeChange={handleSelectionModeChange} />
                  <KNFormDropdown
                    name="direction"
                    label={t('screens.cs.carrier_profile.lanes_and_regions.form.direction')}
                    control={control}
                    options={Object.values(LaneDirection).map((direction) => ({
                      value: direction,
                      label: t(`screens.cs.carrier_profile.lanes_and_regions.direction.${direction}`),
                    }))}
                    rules={{
                      required: t('form.validation.required'),
                    }}
                  />
                  {!additionalInfoPanelVisible && (
                    <Box paddingTop={2}>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={showAdditionalInfoPanel}
                        fullWidth
                      >
                        {t('general.next')}
                      </Button>
                    </Box>
                  )}
                </LaneOrRegionDialogPanel>
              )}
              {additionalInfoPanelVisible && (
                <LaneOrRegionDialogPanel
                  panelType={LaneOrRegionPanel.ADDITIONAL_INFO}
                  header={t(`screens.cs.carrier_profile.lanes_and_regions.additional_info`)}
                  activePanel={activePanel}
                  onPanelChange={handlePanelChange}
                >
                  <KNFormText
                    name="vehiclesAmount"
                    type="number"
                    label={t('screens.cs.carrier_profile.lanes_and_regions.form.number_of_vehicles')}
                    control={control}
                    rules={{
                      required: t('form.validation.required'),
                    }}
                  />
                  <LaneOrRegionDialogContacts contacts={payload.contacts} />
                </LaneOrRegionDialogPanel>
              )}
            </Box>
            <LaneOrRegionDialogActions
              disabled={!additionalInfoPanelVisible}
              onAction={handleSubmitWrapper}
              onClose={onClose}
            />
          </Stack>
        </Stack>
        {recentSelection && (
          <AreaNameDialog
            payload={{
              selection: recentSelection,
            }}
            open={areaNameDialog.isOpen}
            onAction={areaNameDialog.handleAction}
            onClose={areaNameDialog.handleClose}
          />
        )}
      </KNDialog>
    </FormProvider>
  )
}

export default CreateOrEditLaneDialog
